import * as SVG from 'svg.js';
import { GlobalConstants, StageConstants } from './constants';
import { Globals } from './globals';
import { Utils } from './utils';

export namespace StageUtils
{
  export interface WrappedImage
  {
    wrapper: SVG.Doc;
    image: SVG.Image;
    group?: SVG.G;
  }

  export function isPlaceholder(element: Element): boolean
  {
    return (
        getBooleanAttribute(element, StageConstants.ATTRIBUTE_IS_UNCHANGED) &&
        element.getAttribute(StageConstants.ATTRIBUTE_HREF) ===
            element.getAttribute(StageConstants.ATTRIBUTE_PLACEHOLDER_URL));
  }

  export function isAsset(element: Element): boolean
  {
    return getBooleanAttribute(element, StageConstants.ATTRIBUTE_IS_ASSET);
  }

  export function isContentChangeable(element: Element): boolean
  {
    return (Globals.mode === GlobalConstants.MODE_GOD &&
            !element.closest('defs')) ||
        getBooleanAttribute(
            element, StageConstants.ATTRIBUTE_CONTENT_CHANGEABLE);
  }

  export function getBooleanAttribute(
      element: Element, attrName: string, defaultValue = false): boolean
  {
    if (!element.hasAttribute(attrName)) {
      return defaultValue;
    }
    return element.getAttribute(attrName).toLowerCase() === 'true';
  }

  export function svgSelectAsArray(
    parent: SVG.Parent, selector: string): Array<SVG.Element>
  {
    return (<any>parent.select(selector)).members as Array<SVG.Element>;
  }

  export function querySelectorAsArray(
      parent: Element, selector: string): Array<Element>
  {
    return Array.prototype.slice.call(parent.querySelectorAll(selector));
  }

  function elementsBySelectors(
      parent: Element, selectors: Array<string>): Array<Element>
  {
    return Utils.flattenArray(
        selectors.map((selector) =>
            StageUtils.querySelectorAsArray(parent, selector)));
  }

  export function getAllWrappedImages(stage: SVG.Doc): Array<WrappedImage>
  {
    return querySelectorAsArray(stage.node, 'svg')
        .filter((svg) => querySelectorAsArray(svg, 'image')[0])
        .map((wrapper) => <WrappedImage> {
          wrapper: SVG.adopt(wrapper as HTMLElement) as SVG.Doc,
          image: SVG.adopt(
              querySelectorAsArray(wrapper, 'image')[0] as HTMLElement
          ) as SVG.Image,
          group: (<HTMLElement>wrapper.parentNode).tagName === 'g' ?
              SVG.adopt(<HTMLElement>wrapper.parentNode) as SVG.G : undefined
        });
  }

  export function withoutNonBreakingSpaces(text: string): string
  {
    return text.replace(/\&nbsp\;/g, '\u00A0');
  }

  export function ensureHasPapNamespace(structure: string): string
  {
    if (RegExp('xmlns:pap').test(structure)) {
      return structure;
    }

    const startIndex = structure.search(RegExp('svg'));

    if (startIndex === -1) {
      return structure;
    }

    const pre = structure.substring(0, startIndex + 3);
    const pap = ` xmlns:pap="${StageConstants.XML_NAMESPACE_PAP}"`;
    const post = structure.substring(startIndex + 3);
    return pre + pap + post;
  }

  function restoreOriginalViewBox(doc: SVG.Doc): void
  {
    // querySelctor with something like pap:original-viewBox could make
    // problems in many browsers. More information are at
    //  https://stackoverflow.com/a/23047888
    const elements = svgSelectAsArray(doc, 'svg');
    elements.push(doc);
    for (const element of elements) {
      if (element.attr(StageConstants.ATTRIBUTE_ORIGINAL_VIEWBOX)) {
        element.attr(
            'viewBox', element.attr(StageConstants.ATTRIBUTE_ORIGINAL_VIEWBOX));
      }
    }
  }

  export function removeEditorSettings(svgDoc: SVG.Doc): void
  {
    restoreOriginalViewBox(svgDoc);

    const elementsToRemove = elementsBySelectors(
        svgDoc.node,
        StageConstants.DESIGNER_ONLY_CLASSES.map(c => '.' + c));

    for (const element of elementsToRemove) {
      element.remove();
    }

    removeUnusedFonts(svgDoc);
    svgDoc.node.removeAttribute('xmlns:svgjs');
    const viewBoxElement = svgDoc.node.getAttribute('viewBox');
    if (viewBoxElement) {
      const viewBox = viewBoxElement.split(' ');
      svgDoc.width(viewBox[2]).height(viewBox[3]);
    }

    const textElements = elementsBySelectors(svgDoc.node,
        ['text', 'tspan']);

    for (const element of textElements) {
      element.removeAttribute('font-family');
      element.removeAttribute('font-size');
      element.removeAttribute('svgjs:data');
      if (element.hasAttribute('pap:text-model')) {
        element.setAttribute(
            'pap:text-model',
            element.getAttribute('pap:text-model')
              .replace(/,?["']availableArea["']:{[^}]+}/, ''));
      }
    }

    const imagePlaceholders = getChangeableImages(svgDoc);
    for (const image of imagePlaceholders) {
      image.removeClass(
          StageConstants.CLASS_NAME_HIDE_PLACEHOLDER_THROUGH_OPACITY);
    }
  }

  // clean up svg structure
  // removes or alters elements for a reasonable print version
  export function sanitizeStructure(
    structure: string,
    shouldAddPapMetaEntry: boolean = false,
    compact: boolean = false,
    selectSubStructure?: string
  ): string
  {
    structure = structure.replace(/\u00A0/g, ' ');

    const parser = new DOMParser();
    const svg = parser.parseFromString(structure, 'image/svg+xml');
    const svgDocNode = <HTMLElement>svg.firstChild;

    const svgDoc = SVG(
      selectSubStructure
        ? <HTMLElement> (
            StageUtils
              .querySelectorAsArray(svgDocNode, 'svg')
              .filter(
                n => n.getAttribute(
                  StageConstants.ATTRIBUTE_PART_ID) === selectSubStructure))[0]
        : svgDocNode
    );

    removeEditorSettings(svgDoc);

    const stripUnchangedPlaceholders = (_svgDoc) => {
      const unchangedImages = getAllWrappedImages(_svgDoc)
          .filter(element => isPlaceholder(element.image.node))
          .map(element => element.image);
      for (let image of unchangedImages) {
        image.attr('xlink:href', '').attr('pap:href', '');
      }
    };

    const stripWhitePixels = _svgDoc => {
      for (let image of querySelectorAsArray(_svgDoc.node, 'image')
            .filter(i => i.getAttribute('xlink:href').includes(
                StageConstants.PIXEL_WHITE))) {
        image.setAttribute('xlink:href', '');
      }
    };

    const replaceQuotesInStyleAttributes = (_svgDoc) => {
      const elements = querySelectorAsArray(_svgDoc.node, '[style]')
        .map((e) => SVG.adopt(e as HTMLElement) as SVG.Element);
      for (let element of elements) {
        element.attr('style', element.attr('style').replace(/\"/g, '\''));
      }
    };

    const replaceTransparentRects = (_svgDoc) => {
      const rects = querySelectorAsArray(_svgDoc.node, 'rect[fill]')
        .filter((node: HTMLElement) => node.style.fill === 'transparent')
        .map((e) => SVG.adopt(e as HTMLElement) as SVG.Rect);

      for (let rect of rects) {
        rect.fill('none');
        rect.node.style.fill = '';
      }
    };

    const stripTextModels = (_svgDoc) => {
      const texts = querySelectorAsArray(_svgDoc.node, 'text');

      for (const text of texts) {
        if (text.hasAttribute(StageConstants.ATTRIBUTE_TEXT_MODEL)) {
          text.removeAttribute(StageConstants.ATTRIBUTE_TEXT_MODEL);
        }
      }
    };

    const compactTextModels = (_svgDoc) => {
      const texts = querySelectorAsArray(_svgDoc.node, 'text');

      for (const text of texts) {
        if (text.hasAttribute(StageConstants.ATTRIBUTE_TEXT_MODEL)) {
          const model = {
            ...JSON.parse(
              text.getAttribute(StageConstants.ATTRIBUTE_TEXT_MODEL)),
            'text': undefined,
            'availableArea': undefined,
            'fontDescription': undefined,
          };

          text.setAttribute(
            StageConstants.ATTRIBUTE_TEXT_MODEL, JSON.stringify(model));
        }
      }
    };

    const addPapMetaEntry = _svg => {
      const getPapCreator = () => {
        const getMetadata = () => {
          let metadata = _svg.getElementsByTagName('metadata')[0];
          if (!metadata) {
            metadata = document.createElement('metadata');
            _svg.appendChild(metadata);
          }

          return metadata;
        };

        let creator = _svg.getElementsByTagNameNS(
            StageConstants.XML_NAMESPACE_PAP, 'creator')[0];
        if (!creator) {
          const metadata = getMetadata();
          creator = document.createElementNS(
              StageConstants.XML_NAMESPACE_PAP, 'pap:creator');
          metadata.appendChild(creator);
        }
        return creator;
      };

      const createEntry = () => {
        const entry = document.createElementNS(
            StageConstants.XML_NAMESPACE_PAP, 'pap:entry');
        const userAgent = document.createElementNS(
            StageConstants.XML_NAMESPACE_PAP, 'pap:user-agent');
        const viewport = document.createElementNS(
            StageConstants.XML_NAMESPACE_PAP, 'pap:viewport');

        entry.setAttribute(
            'timestamp', Math.round(Date.now() / 1000).toString());
        userAgent.textContent = window.navigator.userAgent;
        viewport.setAttribute('width', window.innerWidth.toString());
        viewport.setAttribute('height', window.innerHeight.toString());

        entry.appendChild(userAgent);
        entry.appendChild(viewport);
        return entry;
      };

      const creator = getPapCreator();
      creator.appendChild(createEntry());
    };

    stripUnchangedPlaceholders(svgDoc);
    stripWhitePixels(svgDoc);
    replaceQuotesInStyleAttributes(svgDoc);
    replaceTransparentRects(svgDoc);
    if (shouldAddPapMetaEntry) {
      addPapMetaEntry(svgDoc.node);
    }
    if (!compact && Globals.mode !== GlobalConstants.MODE_GOD) {
      stripTextModels(svgDoc);
    } else {
      compactTextModels(svgDoc);
    }

    return new XMLSerializer().serializeToString(svgDoc.node);
  }

  export function findParentBySelector(element: Element, selector: string):
      HTMLElement
  {
    const all = Array.from(document.querySelectorAll(selector));
    let parent = element.parentNode;
    while (parent && all.indexOf(<HTMLElement> parent) === -1) {
      parent = parent.parentNode;
    }
    return <HTMLElement> parent;
  }

  export function isAncestorLinked(element: Node): boolean
  {
    while (element = element.parentNode) {
      if (element !== window.document &&
          (<HTMLElement> element).hasAttribute(
              StageConstants.ATTRIBUTE_COPIED_FROM)) {
        return true;
      }
    }

    return false;
  }

  export function getPartDocument(element: SVG.Element): SVG.Element
  {
    const candidates = ((element as any)
                        .parents()
                        .filter(p => !!p.attr(StageConstants.ATTRIBUTE_PART_ID))
                        .concat([(element as any).doc()]));

    return candidates[0];
  }

  export function containerHasZoomMode(element: Element): boolean
  {
    return (element.parentNode as HTMLElement)
      .hasAttribute(StageConstants.ATTRIBUTE_ZOOM_MODE);
  }

  function getChangeableImages(stageReference: SVG.Doc): Array<SVG.Image>
  {
    return getAllWrappedImages(stageReference)
      .filter((el: WrappedImage) =>
          isContentChangeable(el.wrapper.node))
      .map((el: WrappedImage) => el.image);
  }

  export function originalDimensions(doc: SVG.Doc): SVG.ViewBox
  {
    return (<any>doc).dimensions ? (<any>doc).dimensions() : doc.viewbox();
  }

  // frei nach https://developer.mozilla.org/en-US/docs/Web/API/ChildNode/remove
  export function removeElement(elem: Element): void
  {
    if ('remove' in elem && typeof elem.remove === 'function') {
      elem.remove();
    }
    else if (elem.parentNode !== undefined) {
      elem.parentNode.removeChild(elem);
    }
  }

  export function getComponentType(stageReference: SVG.Doc): string | undefined
  {
    return stageReference &&
        stageReference.attr(StageConstants.ATTRIBUTE_COMPONENT_TYPE);
  }

  export function isBoxComponentType(componentType: string): boolean
  {
    return [
      StageConstants.COMPONENT_BOX_LARGE,
      StageConstants.COMPONENT_BOX_LARGE_2023,
      StageConstants.COMPONENT_BOX_SMALL,
    ].indexOf(componentType) !== -1;
  }

  export function removeUnusedFonts(stageReference: SVG.Doc): void
  {
    const fontsContainer = stageReference.node.querySelector('#fonts');

    if (!fontsContainer || fontsContainer.childNodes.length === 0) {
      return;
    }
    const textElements = elementsBySelectors(
        stageReference.node, ['text', 'tspan']);

    let fontFamilies = [];
    for (const elem of textElements) {
      const fontFamily = (<any>elem).style.fontFamily
        .replace(/, /g, ',')
        .replace(/'|"/g, '')
        .split(',');

      if (fontFamily && fontFamily.length > 0) {
        fontFamilies = fontFamilies.concat(fontFamily)
          .filter((el, i, a) => i === a.indexOf(el));
      }
    }

    const isContainerFontUsed = (containerFont: string) => {
      return fontFamilies.some(f => new RegExp(f, 'i').test(containerFont));
    };

    const fontsInContainerAsArray = fontsContainer.textContent
      .match(/\/\*(.|\n)*?font-face(.|\n)*?\*\//gm);
    if (!fontsInContainerAsArray) {
      return;
    }

    fontsContainer.textContent =
        fontsInContainerAsArray.filter(f => isContainerFontUsed(f)).join('');
  }

  export function listElementIds(root: Element): Array<string>
  {
    return (StageUtils
            .querySelectorAsArray(root, '[id]')
            .map(el => el.getAttribute('id')));
  }

  export interface ReferencedElementInfo
  {
    element: Element;
    attribute: string;
    referencedId: string;
    reference: Element;
  }

  export function findReferencedElements(root: Element):
    Array<ReferencedElementInfo>
  {
    const lookupReference =
      (element: Element, attribute: string): ReferencedElementInfo => {
        if (! element.hasAttribute(attribute)) {
          return undefined;
        }

        const value = element.getAttribute(attribute);
        const match = value.match(RegExp('^url[(]#(.*)[)]$'));

        if (! match) {
          return undefined;
        }

        const referencedId = match[1];

        return {
          element: element,
          attribute: attribute,
          referencedId: referencedId,
          reference: root.querySelector(`#${referencedId}`),
        };
      };

    return Utils.flattenArray(
      StageUtils
        .querySelectorAsArray(root, '*')
        .map(el => StageConstants.REFERENCING_SVG_ATTRIBUTES
             .map(a => lookupReference(el, a))
             .filter(it => it !== undefined))
        .filter(it => it.length > 0)
    );
  }

  export function ensureCorrectLayerPositions(doc: SVG.Doc): void
  {
    raiseLayersToFront(doc, [StageConstants.GROUP_TAG_DESIGN_ONLY,
                             StageConstants.GROUP_TAG_OVERLAYS]);
  }

  export function raiseLayersToFront(
    doc: SVG.Doc, layerTags: Array<string>): void
  {
    const setToFront = svg => {
      const layers = layerTags
        .map(tag => svg.select(`#${svg.id()} > .${tag}`).first())
        .filter(l => !!l);

      for (const layer of layers) {
        layer.front();
      }
    };

    doc.select('svg').each((i, e) => setToFront(e[i]));
    setToFront(doc);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/utils.stage.ts